/** Whether a fill-in-blank question is complete (has at least one answer blank). */
function isCompleteFillInBlankQuestion(question) {
    return Boolean(question?.blocks?.some((block) => block.type === 'answer'));
}

/** Whether a multiple-choice question is complete. */
function isCompleteMultipleChoiceQuestion(question) {
    const text = (question?.text || question?.question || '').trim();
    const choices = (question?.choices || []).filter((choice) => {
        if (typeof choice === 'string') {
            return choice.trim();
        }
        return (choice?.value || choice?.text || '').trim();
    });
    return Boolean(text) && choices.length >= 2;
}

function normalizeQuestionForEditor(sectionType, question) {
    if (!question || question.stage === undefined) {
        return question;
    }

    let complete = false;
    if (sectionType === 'fill-in-blank') {
        complete = isCompleteFillInBlankQuestion(question);
    } else if (sectionType === 'multiple-choice') {
        complete = isCompleteMultipleChoiceQuestion(question);
    }

    if (!complete) {
        return question;
    }

    const normalized = Object.assign({}, question);
    delete normalized.stage;
    delete normalized.autoFocus;
    return normalized;
}

/** True when any question in the section is still in an editor stage. */
export function sectionQuestionsInEdit(questions) {
    return (questions || []).some((question) => question.stage !== undefined);
}

/** Prepare paper/sections loaded from Firestore (or AI) for the paper editor. */
export function normalizeLoadedPaper(paper) {
    if (!paper || !Array.isArray(paper.sections)) {
        return paper;
    }

    const sections = paper.sections.map((section) => {
        const questions = (section.questions || []).map((question) =>
            normalizeQuestionForEditor(section.type, question)
        );
        return Object.assign({}, section, {
            questions,
            isEditing: sectionQuestionsInEdit(questions),
        });
    });

    return Object.assign({}, paper, {
        sections,
        grade: paper.grade ?? null,
        school: paper.school ?? null,
        subtitle: paper.subtitle ?? null,
    });
}

/** Firestore rejects undefined; convert recursively to null. */
export function sanitizeForFirestore(value) {
    if (value === undefined) {
        return null;
    }
    if (value === null || typeof value !== 'object') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(sanitizeForFirestore);
    }
    if (typeof value.toDate === 'function') {
        return value;
    }

    const result = {};
    Object.keys(value).forEach((key) => {
        result[key] = sanitizeForFirestore(value[key]);
    });
    return result;
}
